import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';

/* ─── Highlights Data ──────────────────────────────────────────── */
const highlights = [
    { icon: "fal fa-flask",     label: "Pharmaceutics & Pharmacology Labs" },
    { icon: "fal fa-microscope", label: "Research & Industry Exposure" },
    { icon: "fal fa-user-md",   label: "Healthcare & Clinical Training" },
    { icon: "fal fa-briefcase", label: "Career & Placement Support" },
];

const pills = [
    "4 Years · 8 Semesters",
    "PCM / PCB / PCMB",
    "Pharmacy Council of India Approved",
    "RGUHS Affiliated",
    "Govt. of Karnataka Approved",
];

/* ─── Framer Motion Variants ───────────────────────────────────── */
const fadeDown = {
    hidden: { opacity: 0, y: -40 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
    },
};

const fadeUp = {
    hidden: { opacity: 0, y: 50 },
    visible: (delay = 0) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1], delay },
    }),
};

const pillContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.35 } },
};

const pillVariants = {
    hidden: { opacity: 0, scale: 0.75, y: 10 },
    visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        transition: { duration: 0.45, ease: [0.34, 1.56, 0.64, 1] },
    },
};

const highlightContainerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.13, delayChildren: 0.5 } },
};

const highlightItemVariants = {
    hidden: { opacity: 0, x: -30 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.5, ease: 'easeOut' },
    },
};

/* ─── Main Component ───────────────────────────────────────────── */
function CourseCard() {
    /* Section title */
    const { ref: titleRef, inView: titleInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* Card wrapper */
    const { ref: cardRef, inView: cardInView } = useInView({ threshold: 0.2, triggerOnce: true });

    /* GSAP: card border glow on enter */
    const cardElRef = useRef(null);
    useEffect(() => {
        if (cardInView && cardElRef.current) {
            gsap.fromTo(
                cardElRef.current,
                { boxShadow: '0 0 0px rgba(30,90,168,0)' },
                {
                    boxShadow: '0 8px 40px rgba(30,90,168,0.14)',
                    duration: 1.2,
                    ease: 'power2.out',
                    delay: 0.5,
                }
            );
        }
    }, [cardInView]);

    /* GSAP: blue header shimmer sweep */
    const headerRef = useRef(null);
    const shimmerRef = useRef(null);
    useEffect(() => {
        if (cardInView && shimmerRef.current) {
            gsap.fromTo(
                shimmerRef.current,
                { x: '-100%', opacity: 0.6 },
                { x: '200%', opacity: 0, duration: 1.0, ease: 'power2.inOut', delay: 0.7 }
            );
        }
    }, [cardInView]);

    /* Merge refs helper */
    const setCardRefs = (el) => {
        cardElRef.current = el;
        cardRef(el);
    };

    return (
        <section
            className="shop-area pt-120 pb-120 p-relative"
            style={{ background: '#f5f8ff' }}
        >
            <div className="container">

                {/* ── Section Title ── */}
                <div className="row">
                    <div className="col-lg-12">
                        <motion.div
                            ref={titleRef}
                            className="section-title text-center mb-50"
                            variants={fadeDown}
                            initial="hidden"
                            animate={titleInView ? 'visible' : 'hidden'}
                        >
                            <h5><i className="fal fa-graduation-cap" /> Our Courses</h5>
                            <h2>Programs Offered</h2>
                        </motion.div>
                    </div>
                </div>

                {/* ── Course Card ── */}
                <div className="row justify-content-center">
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <motion.div
                            ref={setCardRefs}
                            className="courses-item mb-30"
                            variants={fadeUp}
                            custom={0}
                            initial="hidden"
                            animate={cardInView ? 'visible' : 'hidden'}
                            style={{
                                border: '1px solid #b8d0f0',
                                borderRadius: '14px',
                                overflow: 'hidden',
                                background: '#fff',
                            }}
                        >
                            {/* ── Blue Header ── */}
                            <div
                                ref={headerRef}
                                style={{
                                    background: '#1E5AA8',
                                    padding: '1.75rem 2rem 1.5rem',
                                    position: 'relative',
                                    overflow: 'hidden',
                                }}
                            >
                                {/* Shimmer overlay (GSAP) */}
                                <div
                                    ref={shimmerRef}
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '60%',
                                        height: '100%',
                                        background: 'linear-gradient(105deg, transparent 20%, rgba(255,255,255,0.25) 50%, transparent 80%)',
                                        pointerEvents: 'none',
                                    }}
                                />

                                <motion.span
                                    variants={fadeUp}
                                    custom={0.2}
                                    initial="hidden"
                                    animate={cardInView ? 'visible' : 'hidden'}
                                    style={{
                                        display: 'inline-block',
                                        background: 'rgba(255,255,255,0.2)',
                                        color: '#fff',
                                        fontSize: '11px',
                                        fontWeight: 500,
                                        padding: '4px 14px',
                                        borderRadius: '20px',
                                        border: '1px solid rgba(255,255,255,0.35)',
                                        marginBottom: '0.85rem',
                                        letterSpacing: '0.06em',
                                    }}
                                >
                                    Undergraduate Program
                                </motion.span>

                                <motion.h3
                                    variants={fadeUp}
                                    custom={0.3}
                                    initial="hidden"
                                    animate={cardInView ? 'visible' : 'hidden'}
                                    style={{ fontSize: '32px', fontWeight: 700, color: '#fff', margin: '0 0 6px' }}
                                >
                                    B.Pharmacy
                                </motion.h3>

                                <motion.p
                                    variants={fadeUp}
                                    custom={0.4}
                                    initial="hidden"
                                    animate={cardInView ? 'visible' : 'hidden'}
                                    style={{ color: 'rgba(255,255,255,0.8)', fontSize: '14px', margin: 0 }}
                                >
                                    Bachelor of Pharmacy — Anjanadri Institute of Pharmacy
                                </motion.p>
                            </div>

                            {/* ── Pills ── */}
                            <motion.div
                                style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', padding: '1.25rem 2rem 0' }}
                                variants={pillContainerVariants}
                                initial="hidden"
                                animate={cardInView ? 'visible' : 'hidden'}
                            >
                                {pills.map((pill, i) => (
                                    <motion.span
                                        key={i}
                                        variants={pillVariants}
                                        whileHover={{ scale: 1.07, y: -2 }}
                                        style={{
                                            fontSize: '12px',
                                            fontWeight: 500,
                                            color: '#F27024',
                                            background: '#fff4ec',
                                            border: '1px solid #ffd4b0',
                                            padding: '5px 14px',
                                            borderRadius: '20px',
                                            cursor: 'default',
                                        }}
                                    >
                                        {pill}
                                    </motion.span>
                                ))}
                            </motion.div>

                            {/* ── Description ── */}
                            <motion.div
                                style={{ padding: '1.25rem 2rem' }}
                                variants={fadeUp}
                                custom={0.45}
                                initial="hidden"
                                animate={cardInView ? 'visible' : 'hidden'}
                            >
                                <p style={{ fontSize: '14px', color: '#374151', lineHeight: 1.8, margin: 0 }}>
                                    The Bachelor of Pharmacy (B.Pharm) is a four-year, eight-semester undergraduate program
                                    designed to prepare students for a professional career in pharmaceutical sciences.
                                    The program provides comprehensive knowledge of drug formulation, pharmacology,
                                    medicinal chemistry, pharmacognosy, and pharmacy practice — with a strong focus on
                                    academic excellence, practical training, research exposure, and industry readiness.
                                </p>
                            </motion.div>

                            {/* ── Highlights Grid ── */}
                            <motion.div
                                style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', padding: '0 2rem 1.5rem' }}
                                variants={highlightContainerVariants}
                                initial="hidden"
                                animate={cardInView ? 'visible' : 'hidden'}
                            >
                                {highlights.map((item, i) => (
                                    <motion.div
                                        key={i}
                                        variants={highlightItemVariants}
                                        whileHover={{
                                            scale: 1.04,
                                            background: '#ddeeff',
                                            transition: { duration: 0.2 },
                                        }}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                            background: '#f0f6ff',
                                            border: '1px solid #b8d0f0',
                                            borderRadius: '8px',
                                            padding: '10px 16px',
                                            fontSize: '13px',
                                            color: '#1E5AA8',
                                            fontWeight: 500,
                                            flex: '1 1 200px',
                                            cursor: 'default',
                                        }}
                                    >
                                        <i className={item.icon} style={{ fontSize: '16px', color: '#F27024' }} />
                                        {item.label}
                                    </motion.div>
                                ))}
                            </motion.div>

                            {/* ── Divider ── */}
                            <motion.div
                                style={{ borderTop: '1px solid #e5edf8', margin: '0 2rem' }}
                                initial={{ scaleX: 0, originX: 0 }}
                                animate={cardInView ? { scaleX: 1 } : { scaleX: 0 }}
                                transition={{ duration: 0.8, ease: 'easeOut', delay: 0.7 }}
                            />

                            {/* ── Footer ── */}
                            <motion.div
                                style={{
                                    padding: '1.25rem 2rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    flexWrap: 'wrap',
                                    gap: '1rem',
                                }}
                                variants={fadeUp}
                                custom={0.75}
                                initial="hidden"
                                animate={cardInView ? 'visible' : 'hidden'}
                            >
                                <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                                    <i className="fal fa-map-marker-alt" style={{ color: '#F27024', marginRight: '6px' }} />
                                    Anjanadri institute of pharmacy, Sy. No. 43/P-1, Doddajala village, Jala hobli, International Airport Road, Bangalore - 562157
                                </p>

                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.97 }}
                                    style={{ display: 'inline-block' }}
                                >
                                    <Link to="/courses" className="btn ss-btn">
                                        View Course Details <i className="fal fa-long-arrow-right" />
                                    </Link>
                                </motion.div>
                            </motion.div>

                        </motion.div>
                    </div>
                </div>

            </div>
        </section>
    );
}

export default CourseCard;
